import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import {
  getInquiries,
  deleteInquiry,
  updateInquiryStatus,
} from "../utils/inquiryStorage";
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Trash2,
  LogOut,
  CheckCircle,
  Clock,
  X,
  Package,
  MessageSquare,
  User,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const AdminInquiries = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const loadInquiries = useCallback(async () => {
    try {
      const loadedInquiries = await getInquiries();
      setInquiries(loadedInquiries);
    } catch (error) {
      console.error("Error loading inquiries:", error);
      toast({
        title: "Unable to Load Inquiries",
        description: "Please check your Supabase setup and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    loadInquiries();
  }, [user, navigate, loadInquiries]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this inquiry?")) {
      try {
        await deleteInquiry(id);
        await loadInquiries();
        toast({
          title: "Inquiry Deleted",
          description: "Inquiry has been deleted successfully.",
        });
      } catch (error) {
        console.error("Error deleting inquiry:", error);
        toast({
          title: "Delete Failed",
          description: "The inquiry could not be deleted.",
          variant: "destructive",
        });
      }
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateInquiryStatus(id, status);
      await loadInquiries();
      toast({
        title: "Status Updated",
        description: `Inquiry marked as ${status}.`,
      });
    } catch (error) {
      console.error("Error updating inquiry status:", error);
      toast({
        title: "Status Update Failed",
        description: "The inquiry status could not be updated.",
        variant: "destructive",
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const filteredInquiries =
    filter === "all"
      ? inquiries
      : inquiries.filter((i) => i.status === filter);

  const stats = {
    total: inquiries.length,
    new: inquiries.filter((i) => i.status === "new").length,
    inProgress: inquiries.filter((i) => i.status === "in-progress").length,
    completed: inquiries.filter((i) => i.status === "completed").length,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/blog")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Inquiries Management
                </h1>
                <p className="text-sm text-gray-600">
                  Welcome, {user?.name || user?.username}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-3xl font-bold text-purple-600 mb-2">
              {stats.total}
            </h3>
            <p className="text-gray-600">Total Inquiries</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-3xl font-bold text-blue-600 mb-2">
              {stats.new}
            </h3>
            <p className="text-gray-600">New</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-3xl font-bold text-yellow-600 mb-2">
              {stats.inProgress}
            </h3>
            <p className="text-gray-600">In Progress</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-3xl font-bold text-green-600 mb-2">
              {stats.completed}
            </h3>
            <p className="text-gray-600">Completed</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {["all", "new", "in-progress", "completed"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === status
                  ? "bg-purple-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {status === "all"
                ? "All"
                : status
                    .split("-")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
            </button>
          ))}
        </div>

        {/* Inquiries List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">All Inquiries</h2>
          </div>

          {filteredInquiries.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No inquiries found.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredInquiries.map((inquiry) => (
                <motion.div
                  key={inquiry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            inquiry.status === "new"
                              ? "bg-blue-100 text-blue-700"
                              : inquiry.status === "in-progress"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {inquiry.status
                            .split("-")
                            .map((word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")}
                        </span>
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            inquiry.type === "contact"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-pink-100 text-pink-700"
                          }`}
                        >
                          {inquiry.type === "contact"
                            ? "Contact Form"
                            : "Quick Inquiry"}
                        </span>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(inquiry.createdAt)}</span>
                        </div>
                      </div>

                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {inquiry.fullName || inquiry.firstName + " " + inquiry.lastName}
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4" />
                          <span>{inquiry.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{inquiry.phone}</span>
                        </div>
                      </div>

                      {inquiry.subject && (
                        <div className="mb-2">
                          <span className="text-sm font-semibold text-gray-700">
                            Subject:{" "}
                          </span>
                          <span className="text-sm text-gray-600">
                            {inquiry.subject}
                          </span>
                        </div>
                      )}

                      {inquiry.product && (
                        <div className="mb-2">
                          <span className="text-sm font-semibold text-gray-700">
                            Product:{" "}
                          </span>
                          <span className="text-sm text-gray-600">
                            {inquiry.product}
                          </span>
                        </div>
                      )}

                      <div className="mb-3">
                        <span className="text-sm font-semibold text-gray-700">
                          Message:{" "}
                        </span>
                        <p className="text-sm text-gray-600 mt-1">
                          {inquiry.message}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <select
                        value={inquiry.status}
                        onChange={(e) =>
                          handleStatusChange(inquiry.id, e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="new">New</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      <button
                        onClick={() => handleDelete(inquiry.id)}
                        className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInquiries;
